import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { 
    ChartBarIcon, 
    ArrowTrendingUpIcon, 
    CurrencyDollarIcon,
    ShoppingBagIcon,
    EyeIcon,
    ArrowUpIcon,
    ArrowDownIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';

function CategoryAnalyticsPanel() {
    const { token } = useAuth();
    const [analytics, setAnalytics] = useState({
        categoryStats: [],
        topCategories: [],
        revenueByCategory: [],
        loading: true
    });

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        if (!token) return;

        try {
            setAnalytics(prev => ({ ...prev, loading: true }));
            
            // Kategori analytics API'sini çağır
            const response = await api.get('/api/analytics/categories', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Debug logları kaldırıldı - Analytics çalışıyor
            
            // Response yapısını düzelt
            const responseData = response.data || response;
            
            setAnalytics({
                categoryStats: responseData.categoryStats || [],
                topCategories: responseData.categoryStats?.slice(0, 5) || [],
                revenueByCategory: responseData.categoryStats || [],
                totalCategories: responseData.totalCategories || 0,
                activeCategories: responseData.activeCategories || 0,
                topCategory: responseData.topCategory,
                totalRevenue: responseData.totalRevenue || 0,
                loading: false
            });
        } catch (error) {
            console.error('Analytics yüklenirken hata:', error);
            toast.error('Analytics verileri yüklenemedi');
            setAnalytics(prev => ({ ...prev, loading: false }));
        }
    };

    const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = "blue" }) => (
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-brand-green">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                    {trend && (
                        <div className={`flex items-center mt-2 text-sm ${
                            trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                            {trend === 'up' ? (
                                <ArrowUpIcon className="h-4 w-4 mr-1" />
                            ) : (
                                <ArrowDownIcon className="h-4 w-4 mr-1" />
                            )}
                            {trendValue}
                        </div>
                    )}
                </div>
                <div className="p-3 bg-brand-green/10 rounded-full">
                    <Icon className="h-8 w-8 text-brand-green" />
                </div>
            </div>
        </div>
    );

    if (analytics.loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Başlık */}
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Kategori Analytics</h2>
                <button
                    onClick={fetchAnalytics}
                    className="px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green/90 transition-colors"
                >
                    Yenile
                </button>
            </div>

            {/* İstatistik Kartları */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Toplam Kategori"
                    value={analytics.totalCategories || 0}
                    icon={ChartBarIcon}
                />
                <StatCard
                    title="Aktif Kategoriler"
                    value={analytics.activeCategories || 0}
                    icon={EyeIcon}
                />
                <StatCard
                    title="En Popüler Kategori"
                    value={analytics.topCategory?.name || 'N/A'}
                    icon={ArrowTrendingUpIcon}
                />
                <StatCard
                    title="Toplam Satış"
                    value={`₺${analytics.totalRevenue || 0}`}
                    icon={CurrencyDollarIcon}
                />
            </div>

            {/* Kategori Performans Tablosu */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Kategori Performansı</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Kategori
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ürün Sayısı
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Toplam Satış
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Gelir
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Durum
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {analytics.categoryStats?.length > 0 ? (
                                analytics.categoryStats.map((category) => (
                                    <tr key={category.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {category.icon && (
                                                    <i className={`${category.icon} text-brand-green mr-3`}></i>
                                                )}
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {category.name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {category.slug}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {category.productCount || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {category.totalSales || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₺{category.revenue || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                category.is_active 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {category.is_active ? 'Aktif' : 'Pasif'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        Henüz kategori analytics verisi bulunmuyor
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Kategori Gelir Grafiği */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kategori Bazlı Gelir Dağılımı</h3>
                <div className="space-y-4">
                    {analytics.categoryStats?.slice(0, 5).map((category, index) => {
                        const maxRevenue = Math.max(...(analytics.categoryStats?.map(c => c.revenue) || [1]));
                        const percentage = maxRevenue > 0 ? (category.revenue / maxRevenue) * 100 : 0;
                        
                        return (
                            <div key={category.id} className="flex items-center space-x-4">
                                <div className="w-24 text-sm font-medium text-gray-700 truncate">
                                    {category.name}
                                </div>
                                <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                                    <div 
                                        className="bg-gradient-to-r from-brand-green to-brand-green/80 h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                                        style={{ width: `${Math.max(percentage, 5)}%` }}
                                    >
                                        <span className="text-white text-xs font-medium">
                                            ₺{category.revenue || 0}
                                        </span>
                                    </div>
                                </div>
                                <div className="w-16 text-sm text-gray-600 text-right">
                                    {category.totalSales || 0} adet
                                </div>
                            </div>
                        );
                    })}
                    
                    {(!analytics.categoryStats || analytics.categoryStats.length === 0) && (
                        <div className="text-center text-gray-500 py-8">
                            <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                            <p>Henüz satış verisi bulunmuyor</p>
                            <p className="text-sm mt-1">Siparişler tamamlandıkça veriler burada görünecek</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CategoryAnalyticsPanel;